// components/HomePage/Navbar.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ChevronDown, ChevronRight, Mail, Menu, Phone, X } from "lucide-react";

import {
  PRIMARY_LINKS,
  NAV_CTA,
  SERVICE_GROUPS,
  SERVICE_COUNT,
  isServicesPath,
} from "../../data/navigation";
import { CONTACT, MAILTO, TEL } from "../../config/site";

const HOVER_OPEN_DELAY = 90;
const HOVER_CLOSE_DELAY = 220;

// Written to imperatively by the scroll listener, so it is declared once
// and never diffed back over the value that listener just set.
const PROGRESS_STYLE = { transform: "scaleX(0)", willChange: "transform" };

const pad2 = (n) => String(n).padStart(2, "0");
const cx = (...parts) => parts.filter(Boolean).join(" ");

const Navbar = () => {
  const location = useLocation();
  const isHome = location.pathname === "/";

  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // First accordion group starts open so the drawer never reads as a dead end.
  const [openGroup, setOpenGroup] = useState(
    SERVICE_GROUPS.length ? SERVICE_GROUPS[0].key : null
  );
  const [logoActive, setLogoActive] = useState(false);

  const scrolledRef = useRef(false);
  const progressRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const drawerRef = useRef(null);
  const toggleRef = useRef(null);
  const closeRef = useRef(null);
  const hoverTimer = useRef(null);
  const hoverCapable = useRef(false);
  const wasOpen = useRef(false);

  // Solid bar everywhere except the top of the homepage hero, which is the
  // site's signature transparent-over-video first impression.
  const solid = scrolled || !isHome || servicesOpen;

  /* Scroll: one passive, rAF-throttled listener drives both the
     transparent→solid flip (setState only when the boolean changes) and
     the reading-progress hairline (written straight to the DOM node). */
  useEffect(() => {
    let frame = 0;

    const read = () => {
      frame = 0;
      const y = window.scrollY || 0;
      if ((y > 8) !== scrolledRef.current) {
        scrolledRef.current = y > 8;
        setScrolled(y > 8);
      }
      const bar = progressRef.current;
      if (bar) {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const ratio = max > 0 ? Math.min(Math.max(y / max, 0), 1) : 0;
        bar.style.transform = `scaleX(${ratio})`;
      }
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(read);
    };

    read(); // correct on a reload part-way down the page
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  useEffect(() => {
    hoverCapable.current =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  }, []);

  // Close both menus on navigation; the router puts the new route at the top.
  useEffect(() => {
    setServicesOpen(false);
    setDrawerOpen(false);
    if (progressRef.current) progressRef.current.style.transform = "scaleX(0)";
  }, [location.pathname]);

  /* Body scroll lock: restores the previous value instead of clobbering it
     with "unset", and releases on unmount. */
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [drawerOpen]);

  /* The drawer is md:hidden, so widening past 768px used to hide it while
     leaving the scroll lock on — the whole page froze. */
  useEffect(() => {
    if (!drawerOpen || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = (event) => {
      if (event.matches) setDrawerOpen(false);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [drawerOpen]);

  // Escape closes; Tab is trapped inside the open drawer.
  useEffect(() => {
    if (!servicesOpen && !drawerOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        if (drawerOpen) setDrawerOpen(false);
        else {
          setServicesOpen(false);
          if (triggerRef.current) triggerRef.current.focus();
        }
        return;
      }
      if (event.key !== "Tab" || !drawerOpen || !drawerRef.current) return;

      const items = drawerRef.current.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [servicesOpen, drawerOpen]);

  /* Mega-menu closes on outside click and when focus leaves it altogether.
     Both listeners exist only while it is open. */
  useEffect(() => {
    if (!servicesOpen) return undefined;
    const onOutside = (event) => {
      const inPanel = panelRef.current && panelRef.current.contains(event.target);
      const inTrigger = triggerRef.current && triggerRef.current.contains(event.target);
      if (!inPanel && !inTrigger) setServicesOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("focusin", onOutside);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("focusin", onOutside);
    };
  }, [servicesOpen]);

  // Focus moves into the drawer on open and back to the toggle on close.
  useEffect(() => {
    if (drawerOpen) {
      wasOpen.current = true;
      const id = window.requestAnimationFrame(() => {
        if (closeRef.current) closeRef.current.focus();
      });
      return () => window.cancelAnimationFrame(id);
    }
    if (wasOpen.current) {
      wasOpen.current = false;
      if (toggleRef.current) toggleRef.current.focus();
    }
    return undefined;
  }, [drawerOpen]);

  const clearHover = useCallback(() => {
    if (hoverTimer.current) {
      window.clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  }, []);

  useEffect(() => clearHover, [clearHover]);

  // Hover-intent, pointer devices only: a short delay in both directions
  // stops the panel flickering as the cursor crosses "Services".
  const hoverOpen = useCallback(() => {
    if (!hoverCapable.current) return;
    clearHover();
    hoverTimer.current = window.setTimeout(() => setServicesOpen(true), HOVER_OPEN_DELAY);
  }, [clearHover]);

  const hoverClose = useCallback(() => {
    if (!hoverCapable.current) return;
    clearHover();
    hoverTimer.current = window.setTimeout(() => setServicesOpen(false), HOVER_CLOSE_DELAY);
  }, [clearHover]);

  const toggleServices = useCallback(() => {
    clearHover();
    setServicesOpen((open) => !open);
  }, [clearHover]);

  // Tapping the logo plays the mark→wordmark morph on touch (desktop uses
  // :hover); the link still navigates home.
  const onLogoClick = () => {
    if (window.innerWidth < 768) setLogoActive((active) => !active);
  };

  const closeDrawer = () => setDrawerOpen(false);
  const closeServices = () => setServicesOpen(false);

  const here = location.pathname.toLowerCase();
  const current = (to) => here === to.toLowerCase();
  const invert = solid ? "" : "brightness-0 invert";
  const servicesActive = isServicesPath(here);

  const navLink = (active) =>
    cx(
      "group relative inline-flex items-center gap-1 py-2 text-[0.9rem] font-medium transition-colors duration-200",
      solid
        ? active ? "text-brand-700" : "text-ink-600 hover:text-brand-700"
        : active ? "text-white" : "text-white/80 hover:text-white"
    );

  // Hairline that grows from the left on hover and stays put on the current page.
  const underline = (active) =>
    cx(
      "pointer-events-none absolute inset-x-0 bottom-0 h-[2px] origin-left rounded-full transition-transform duration-200 ease-out-expo",
      solid ? "bg-brand-500" : "bg-white",
      active ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
    );

  const serviceRow = (to, extra) =>
    cx(
      "flex items-center justify-between gap-3 rounded-lg transition-colors duration-200",
      current(to) ? "bg-brand-50 font-medium text-brand-800" : "text-ink-700",
      extra
    );

  return (
    <>
      <header
        className={cx(
          "fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color] duration-300",
          solid
            ? "border-brand-100 bg-brand-50/85 backdrop-blur-xl"
            : "border-transparent bg-transparent"
        )}
      >
        <nav aria-label="Primary" className="container-custom">
          <div className="flex h-16 items-center justify-between gap-4 md:h-[4.5rem]">
            <Link
              to="/"
              onClick={onLogoClick}
              aria-label="DepthLogix, home"
              className={cx("logo-container-nav", logoActive && "active")}
            >
              {/* Both marks are decorative: the link carries the name. */}
              <img src="/BG/Logo.png" alt="" aria-hidden="true" width={80} height={50}
                decoding="async" className={cx("logo-nav", invert)} />
              <img src="/BG/depthlogix.png" alt="" aria-hidden="true" width={127} height={20}
                decoding="async" className={cx("logo-text-nav h-5 w-auto", invert)} />
            </Link>

            <ul className="hidden items-center gap-7 md:flex lg:gap-9">
              {PRIMARY_LINKS.map((item) =>
                item.hasMegaMenu ? (
                  <li key={item.label} onMouseEnter={hoverOpen} onMouseLeave={hoverClose}>
                    <button
                      type="button"
                      ref={triggerRef}
                      id="services-trigger"
                      onClick={toggleServices}
                      aria-expanded={servicesOpen}
                      aria-controls="services-menu"
                      className={navLink(servicesActive)}
                    >
                      {item.label}
                      <ChevronDown aria-hidden="true" strokeWidth={2}
                        className={cx("h-3.5 w-3.5 transition-transform duration-200",
                          servicesOpen && "rotate-180")} />
                      <span aria-hidden="true" className={underline(servicesActive)} />
                    </button>
                  </li>
                ) : (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      aria-current={current(item.to) ? "page" : undefined}
                      className={navLink(current(item.to))}
                    >
                      {item.label}
                      <span aria-hidden="true" className={underline(current(item.to))} />
                    </Link>
                  </li>
                )
              )}
            </ul>

            <div className="flex items-center gap-2">
              <Link
                to={NAV_CTA.to}
                className={cx(
                  "group hidden items-center gap-2 rounded-full px-5 py-2.5 text-[0.85rem] font-semibold shadow-card md:inline-flex",
                  "transition-[background-color,color,box-shadow,transform] duration-200 hover:-translate-y-0.5",
                  solid
                    ? "bg-brand-800 text-white hover:bg-brand-700 hover:shadow-card-hover"
                    : "bg-white text-brand-900 hover:bg-brand-100"
                )}
              >
                {NAV_CTA.label}
                <ArrowRight aria-hidden="true"
                  className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>

              <button
                type="button"
                ref={toggleRef}
                onClick={() => setDrawerOpen(true)}
                aria-expanded={drawerOpen}
                aria-controls="mobile-nav"
                className={cx(
                  "inline-flex h-11 w-11 items-center justify-center rounded-xl transition-colors duration-200 md:hidden",
                  solid ? "text-ink-700 hover:bg-brand-100" : "text-white hover:bg-white/10"
                )}
              >
                <span className="sr-only">Open menu</span>
                <Menu aria-hidden="true" className="h-5 w-5" />
              </button>
            </div>
          </div>
        </nav>

        {/* Mega-menu. `hidden` below md keeps 25 links out of the mobile tab
            order; `invisible` does the same on desktop while it is closed. */}
        <div
          ref={panelRef}
          id="services-menu"
          onMouseEnter={hoverOpen}
          onMouseLeave={hoverClose}
          className={cx(
            "absolute inset-x-0 top-full hidden pb-6 pt-2 md:block",
            "transition-[opacity,transform,visibility] duration-200 ease-out-expo",
            servicesOpen
              ? "visible translate-y-0 opacity-100"
              : "invisible -translate-y-2 opacity-0"
          )}
        >
          <div className="container-custom">
            <div className="mx-auto max-h-[calc(100vh-6rem)] max-w-6xl overflow-y-auto rounded-panel border border-brand-100 bg-white shadow-panel">
              {/* gap-px over a tinted background gives hairline column rules. */}
              <div className="grid gap-px bg-brand-100 md:grid-cols-2 xl:grid-cols-4">
                {SERVICE_GROUPS.map((group) => (
                  <div key={group.key} className="bg-white p-5 lg:p-6">
                    <div className="flex items-baseline justify-between gap-3">
                      {/* A <p>, not a heading: the navbar must not inject
                          headings into every page's document outline. */}
                      <p id={`mm-${group.key}`}
                        className="font-bricolage text-[0.95rem] font-semibold leading-snug text-ink-900">
                        {group.label}
                      </p>
                      <span aria-hidden="true" className="font-mono text-[0.65rem] tabular-nums text-ink-400">
                        {pad2(group.items.length)}
                      </span>
                    </div>
                    <p className="mt-2 max-w-[34ch] text-[0.78rem] leading-relaxed text-ink-500">
                      {group.blurb}
                    </p>
                    <ul className="mt-4 space-y-px" aria-labelledby={`mm-${group.key}`}>
                      {group.items.map((service) => (
                        <li key={service.to}>
                          <Link
                            to={service.to}
                            onClick={closeServices}
                            aria-current={current(service.to) ? "page" : undefined}
                            className={serviceRow(
                              service.to,
                              "group/item px-2.5 py-2 text-[0.85rem] leading-snug hover:bg-brand-50 hover:text-brand-800"
                            )}
                          >
                            <span>{service.label}</span>
                            <ChevronRight aria-hidden="true"
                              className="h-3.5 w-3.5 flex-shrink-0 text-brand-300 opacity-0 transition-[opacity,transform] duration-200 group-hover/item:translate-x-0.5 group-hover/item:opacity-100" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-brand-100 bg-ink-50 px-5 py-4 lg:px-6">
                <p className="font-mono text-[0.65rem] uppercase tracking-eyebrow text-ink-500">
                  {SERVICE_COUNT} services · {SERVICE_GROUPS.length} categories
                </p>
                <Link
                  to="/services"
                  onClick={closeServices}
                  className="group/all inline-flex items-center gap-1.5 text-[0.82rem] font-semibold text-brand-700 transition-colors duration-200 hover:text-brand-800"
                >
                  View all {SERVICE_COUNT} services
                  <ArrowRight aria-hidden="true"
                    className="h-3.5 w-3.5 transition-transform duration-200 group-hover/all:translate-x-0.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Reading position — driven straight from the scroll listener. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-px overflow-hidden">
          <div ref={progressRef} style={PROGRESS_STYLE}
            className={cx("h-full w-full origin-left", solid ? "bg-brand-500" : "bg-white/60")} />
        </div>
      </header>

      {/* A sibling of <header> on purpose: the scrolled bar uses backdrop-blur,
          which would otherwise become the containing block for this. */}
      <div
        id="mobile-nav"
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
        className={cx(
          "fixed inset-0 z-[60] flex flex-col bg-white md:hidden",
          "transition-[opacity,transform,visibility] duration-300 ease-out-expo",
          drawerOpen ? "visible translate-x-0 opacity-100" : "invisible translate-x-full opacity-0"
        )}
      >
        <div className="flex h-16 flex-shrink-0 items-center justify-between border-b border-brand-100 px-5">
          <img src="/BG/Logo.png" alt="DepthLogix" width={80} height={50}
            decoding="async" className="h-8 w-auto" />
          <button
            type="button"
            ref={closeRef}
            onClick={closeDrawer}
            className="inline-flex h-11 w-11 items-center justify-center rounded-xl text-ink-600 transition-colors duration-200 hover:bg-brand-50 hover:text-brand-800"
          >
            <span className="sr-only">Close menu</span>
            <X aria-hidden="true" className="h-5 w-5" />
          </button>
        </div>

        <div className="mobile-menu-container flex-1 overflow-y-auto overscroll-contain px-5 pb-8 pt-3">
          <ul>
            {PRIMARY_LINKS.map((item) =>
              item.hasMegaMenu ? (
                <li key={item.label} className="py-3">
                  <div className="flex items-baseline justify-between gap-3 px-1 pb-2">
                    <span className="font-mono text-[0.65rem] uppercase tracking-eyebrow text-ink-400">
                      {item.label}
                    </span>
                    <Link to={item.to} onClick={closeDrawer}
                      className="text-[0.8rem] font-semibold text-brand-700">
                      All {SERVICE_COUNT}
                    </Link>
                  </div>

                  {/* Accordions are conditionally rendered rather than clipped
                      by a max-height, which used to cut off the last group. */}
                  <div className="divide-y divide-brand-100 border-y border-brand-100">
                    {SERVICE_GROUPS.map((group) => {
                      const open = openGroup === group.key;
                      return (
                        <div key={group.key}>
                          <button
                            type="button"
                            onClick={() => setOpenGroup(open ? null : group.key)}
                            aria-expanded={open}
                            aria-controls={`nav-group-${group.key}`}
                            className="flex w-full items-center justify-between gap-3 px-1 py-3.5 text-left"
                          >
                            <span className="font-bricolage text-[0.95rem] font-semibold text-ink-900">
                              {group.label}
                            </span>
                            <span className="flex flex-shrink-0 items-center gap-2">
                              <span className="font-mono text-[0.65rem] tabular-nums text-ink-400">
                                {pad2(group.items.length)}
                              </span>
                              <ChevronDown aria-hidden="true"
                                className={cx("h-4 w-4 text-ink-400 transition-transform duration-200",
                                  open && "rotate-180")} />
                            </span>
                          </button>

                          <div id={`nav-group-${group.key}`} hidden={!open} className="pb-3">
                            <p className="px-1 pb-2 text-[0.78rem] leading-relaxed text-ink-500">
                              {group.blurb}
                            </p>
                            <ul>
                              {group.items.map((service) => (
                                <li key={service.to}>
                                  <Link
                                    to={service.to}
                                    onClick={closeDrawer}
                                    aria-current={current(service.to) ? "page" : undefined}
                                    className={serviceRow(service.to, "px-3 py-3 text-[0.92rem]")}
                                  >
                                    <span>{service.label}</span>
                                    <ChevronRight aria-hidden="true"
                                      className="h-3.5 w-3.5 flex-shrink-0 text-brand-300" />
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </li>
              ) : (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={closeDrawer}
                    aria-current={current(item.to) ? "page" : undefined}
                    className={cx(
                      "flex items-center justify-between gap-3 px-1 py-3.5 text-lg font-medium transition-colors duration-200",
                      current(item.to) ? "text-brand-700" : "text-ink-800"
                    )}
                  >
                    {item.label}
                    {current(item.to) ? (
                      <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-brand-500" />
                    ) : null}
                  </Link>
                </li>
              )
            )}
          </ul>
        </div>

        <div className="flex-shrink-0 border-t border-brand-100 bg-ink-50 px-5 py-5">
          <Link
            to={NAV_CTA.to}
            onClick={closeDrawer}
            className="group flex w-full items-center justify-center gap-2 rounded-full bg-brand-800 px-6 py-3.5 text-[0.95rem] font-semibold text-white transition-colors duration-200 hover:bg-brand-700"
          >
            {NAV_CTA.label}
            <ArrowRight aria-hidden="true"
              className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          </Link>

          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[0.8rem] text-ink-500">
            <a href={TEL} className="inline-flex items-center gap-1.5 transition-colors duration-200 hover:text-brand-700">
              <Phone aria-hidden="true" className="h-3.5 w-3.5" />
              {CONTACT.phoneDisplay}
            </a>
            <a href={MAILTO} className="inline-flex items-center gap-1.5 transition-colors duration-200 hover:text-brand-700">
              <Mail aria-hidden="true" className="h-3.5 w-3.5" />
              {CONTACT.email}
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
